import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema} from '/opt/buildhome/repo/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/opt/buildhome/repo/markdoc/tags.js'
import * as nodes from '/opt/buildhome/repo/markdoc/nodes.js'
const functions = {};
const schema = {
tags: tags ? (tags.default || tags) : {},
nodes: nodes ? (nodes.default || nodes) : {},
functions: functions ? (functions.default || functions) : {},
...(config ? (config.default || config) : {}),
};

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Manage Data Sources\ndescription: Allows you to check and delete your data sources programmatically.\n---\n\n## Getting data sources\n\nThe `getSources` is designed to retrieve all unique data sources for a specific project.\n\n```bash\nPOST /api.mendable.ai/v0/getSources\n```\n\n### Request\n\nHere is an example request using cURL. The `api_key` must be a server-side API key which you can create in the [Mendable dashboard](https://mendable.ai/app).\n\n```bash\ncurl -X POST https://api.mendable.ai/v0/getSources \\\n  -H \"Content-Type: application/json\" \\\n  -d '{\n        \"api_key\": \"YOUR_API_KEY\"\n      }'\n```\n\n### Response\n\nThe response will be an array of unique sources for the specified project containing an `id` and `source`:\n\n```bash\n[\n  {\n    \"id\": 52,\n    \"source\": \"https://mendable.ai\"\n  }\n]\n```\n\n### Request Parameters\n\n| Field   | Type   | Required | Description                                      |\n| ------- | ------ | -------- | ------------------------------------------------ |\n| api_key | string | Yes      | Your Mendable API key                            |\n\n## Deleting a data source\n\nThe `deleteSource` is designed to delete a specific data source or all data sources for a specific project. The `api_key` must be a server-side API key which you can create in the [Mendable dashboard](https://mendable.ai/app).\n\n```bash\nPOST /api.mendable.ai/v0/deleteSource\n```\n\n### Request\n\nHere is an example request using cURL:\n\n```bash\ncurl -X POST https://api.mendable.ai/v0/deleteSource \\\n  -H \"Content-Type: application/json\" \\\n  -d '{\n        \"api_key\": \"YOUR_API_KEY\",\n        \"source\": \"SOURCE_TO_DELETE\",\n        \"delete_all\": false\n      }'\n```\n\n### Response\n\nThe response will be a confirmation message indicating that the source(s) have been deleted.\n\n### Request Parameters\n\n| Field      | Type    | Required | Description                                      |\n| ---------- | ------- | -------- | ------------------------------------------------ |\n| api_key    | string  | Yes      | Your Mendable API key                            |\n| source     | string  | No       | The source to delete                             |\n| delete_all | boolean | No       | Whether to delete all sources, defaults to false |\n\nIf the `delete_all` parameter is set to true, all sources for the specified project will be deleted, except those manually added through the \"Teach Model\". If it's set to false or not provided, only the specified source will be deleted. If the `source` parameter is not provided and `delete_all` is set to false, an error will be returned.\n";
const filepath = "/mendable-api/manage-data-sources.md";
const ast = Markdoc.parse(source);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    partials[key] = Markdoc.parse(partials[key]);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
